import React, {Fragment, useCallback, useContext, useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, styled} from '@mui/material';
import {taskStatus} from '../../utilities';
import {useTheme} from '@mui/styles';
import moment from 'moment';
import {ConfirmationDialog, FormDialog, fullDate} from '@management-ui/core';
import {ServiceContext} from '../Services';
import DateForm from '../../modules/admin/forms/DateForm';
import TaskForm from '../../modules/admin/forms/TaskForm';
import {useSelector} from 'react-redux';
import Task from './Task';

const Tasks = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  transition: '0.25s opacity ease-in-out',

  '&.loading': {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  '& h3': {
    fontSize: '1.4em',
    margin: 0,
    padding: theme.spacing(1),
  },

  '& ul': {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
}));

const Row = styled('div')(({theme}) => ({
  borderBottom: `1px solid ${theme.palette.background.default}`,

  '&:first-of-type': {
    borderTop: `1px solid ${theme.palette.background.default}`,
  },
}))

const List = ({open, onClose, tasks, onTaskSelected}) => {
  const theme = useTheme();
  const services = useContext(ServiceContext);
  const user = useSelector(state => state['auth'].user);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [showCompleteRecurring, setShowCompleteRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    const groups = [];
    for (let task of tasks) {
      const status = taskStatus(task);
      const group = status === 'overdue' ? {
        title: 'Overdue',
        colour: theme['palette'].error.main,
        tasks: []
      } : (moment(task.deadline).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD') ? {
        title: 'Due: Today',
        colour: theme['palette'].warning.main,
        tasks: []
      } : {
        title: `Due: ${fullDate(task.deadline)}`,
        colour: moment(task.deadline).isBefore(moment().endOf('day')) ? theme['palette'].error.main : theme['palette'].success.main,
        tasks: []
      });
      let index = groups.findIndex(({title}) => title === group.title);
      if (index < 0) {
        index = groups.length;
        groups.push(group);
      }
      groups[index].tasks.push(task);
    }
    setGroups(groups);
  }, [tasks, theme]);

  const handleToggle = useCallback((task) => {
    setSelected(task);
    if (task.schedule) {
      setShowCompleteRecurring(true);
    } else {
      setShowComplete(true);
    }
  }, []);

  const handleToggleComplete = useCallback((confirmed) => {
    setShowComplete(false);
    if (confirmed) {
      setLoading(true);
      services.task.completeTask(selected).then(() => {
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [selected, services]);

  const handleCompleteRecurring = useCallback(async ({date}) => {
    setLoading(true);
    return services.task.completeTask(selected, date).then(() => {
      setLoading(false);
      setShowCompleteRecurring(false);
    }).catch(() => setLoading(false));
  }, [selected, services]);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">My Tasks</DialogTitle>
        <DialogContent>
          {tasks.length > 0 ? (
            <Tasks className={loading ? 'loading' : ''}>
              {groups.map(({title, colour, tasks}, groupIndex) => (
                <Fragment key={groupIndex}>
                  <h3 style={{color: colour}}>{title}</h3>
                  <ul>
                    {tasks.map((task, taskIndex) => (
                      <Row key={`${groupIndex}-${taskIndex}`}>
                        <Task
                          task={task}
                          onSelect={onTaskSelected}
                          onToggle={handleToggle}
                        />
                      </Row>
                    ))}
                  </ul>
                </Fragment>
              ))}
            </Tasks>
          ) : (
            <p>You don't currently have any assigned tasks.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setShowNew(true)} variant="contained" color="success">
            Add New Task
          </Button>
          <Button onClick={() => onClose()} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={showComplete}
        title="Mark as Complete"
        message="Are you sure you want to mark this task as complete?"
        onClose={handleToggleComplete}
      />
      <FormDialog
        open={showCompleteRecurring}
        title="Select Next Occurrence"
        maxWidth="sm"
        onClose={() => setShowCompleteRecurring(false)}
        render={(props) => (
          <DateForm
            {...props}
            entity={{date: selected?.schedule?.next}}
            onSave={(data) => handleCompleteRecurring(data)}/>
        )}
      />
      <FormDialog
        title="Add New Task"
        open={showNew}
        onClose={() => setShowNew(false)}
        render={(props) => (
          <TaskForm
            {...props}
            task={{assignee_id: user.id, priority: 'Medium'}}
            onSaved={() => setShowNew(false)}/>
        )}
      />
    </>
  );
};

export default List;